import { useTranslation } from 'react-i18next';

import { type UUID } from '~/types/common';

import { LEDGER_ACCOUNT_TYPE } from '~/data/ledgerAccount';

import { i18n } from '~/utils';

import {
  Alert,
  ErrorState,
  Fieldset,
  FormRow,
  Icon,
  LoadingOverlay,
  Reveal,
} from '@vestigas/ui';
import { HttpValidationErrorAlert } from '~/ui/molecules';

import { useLedgerAccountForm } from '../useLedgerAccountForm';

type P = {
  readonly ledgerAccountId?: UUID;
  readonly onCancel: () => void;
  readonly onSuccess: () => void;
} & ComponentStyling;

const fieldLabels = {
  accountType: i18n.t(($) => $.form.accountType.label, {
    ns: 'ledgerAccounts',
  }),
  companyId: i18n.t(($) => $.form.companyId.label, { ns: 'ledgerAccounts' }),
  costType: i18n.t(($) => $.form.costType.label, { ns: 'ledgerAccounts' }),
  name: i18n.t(($) => $.form.name.label, { ns: 'ledgerAccounts' }),
  number: i18n.t(($) => $.form.number.label, { ns: 'ledgerAccounts' }),
  validFrom: i18n.t(($) => $.form.validFrom.label, { ns: 'ledgerAccounts' }),
};

/**
 * Create/edit form for one ledger account version. Takes just
 * the id and loads the account itself, so callers never need to know
 * whether it is ready yet. Renders no dialog chrome of its own so it can
 * also be embedded as a standalone page.
 */
export function LedgerAccountForm({
  className,
  ledgerAccountId,
  onCancel,
  onSuccess,
  style,
}: P) {
  const { t } = useTranslation('ledgerAccounts');

  const {
    autoFocusInputRef,
    collisionError,
    form,
    handleDelete,
    isDeleted,
    isDeleting,
    isEditMode,
    isLoading,
    isPending,
    markDuplicateCheckFieldTouched,
    mutationError,
    queryError,
  } = useLedgerAccountForm({
    ledgerAccountId,
    onSuccess,
  });

  // The account behind a deep-linked id may be gone, permission-restricted,
  // or unreachable. Render the error in place of the form entirely rather
  // than falling through to create-mode defaults, which would let the user
  // "edit" fields that silently belong to a brand new account.
  if (queryError) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <ErrorState
          className={className}
          errorMessage={t(($) => $.form.error.loadFailed)}
          style={style}
        />
      </div>
    );
  }

  return (
    <form
      id="ledger-account-form"
      className={className}
      style={style}
      onSubmit={(event) => {
        event.preventDefault();

        void form.handleSubmit();
      }}
    >
      {isDeleted ? (
        <Alert severity="info" className="mb-4">
          {t(($) => $.form.deleted.alert)}
        </Alert>
      ) : null}
      <Reveal show={Boolean(collisionError)}>
        <Alert severity="error" className="mb-4">
          {collisionError}
        </Alert>
      </Reveal>
      <Reveal
        show={Boolean(mutationError) && mutationError?.response.status !== 409}
      >
        <HttpValidationErrorAlert<'ledgerAccounts', 'form'>
          className="mb-4"
          error={mutationError}
          isSubmitting={isPending}
          fieldTransKey={(field) =>
            Object.hasOwn(fieldLabels, field)
              ? fieldLabels[field as keyof typeof fieldLabels]
              : field
          }
        />
      </Reveal>

      <Fieldset padded={false} bordered={false}>
        <LoadingOverlay isLoading={isLoading} className="-inset-2" />

        <FormRow>
          <form.AppField
            name="number"
            listeners={{
              onChange: () => markDuplicateCheckFieldTouched('number'),
            }}
          >
            {(field) => (
              <field.TextField
                ref={isEditMode ? undefined : autoFocusInputRef}
                className="w-44 flex-none"
                isDisabled={isEditMode || isDeleted}
                label={t(($) => $.form.number.label)}
                placeholder={t(($) => $.form.number.placeholder)}
              />
            )}
          </form.AppField>
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                ref={isEditMode ? autoFocusInputRef : undefined}
                className="flex-2"
                isDisabled={isDeleted}
                label={t(($) => $.form.name.label)}
                placeholder={t(($) => $.form.name.placeholder)}
              />
            )}
          </form.AppField>
        </FormRow>

        <FormRow>
          <form.AppField
            name="validFrom"
            listeners={{
              onChange: () => markDuplicateCheckFieldTouched('validFrom'),
            }}
          >
            {(field) => (
              <field.TextField
                className="w-44 flex-none"
                isDisabled={isDeleted}
                label={t(($) => $.form.validFrom.label)}
                type="date"
              />
            )}
          </form.AppField>
          <form.AppField
            name="companyId"
            listeners={{
              onChange: () => markDuplicateCheckFieldTouched('companyId'),
            }}
          >
            {(field) => (
              <field.MultiSelectCompaniesField
                className="flex-1"
                isClearable={false}
                isDisabled={isEditMode || isDeleted}
                isMultiSelect={false}
                label={t(($) => $.form.companyId.label)}
                placeholder={t(($) => $.form.companyId.placeholder)}
              />
            )}
          </form.AppField>
        </FormRow>

        <FormRow>
          <form.AppField name="accountType">
            {(field) => (
              <field.ToggleButtonField
                className="w-fit shrink-0"
                buttonsClassName="flex-1 min-w-32"
                label={t(($) => $.form.accountType.label)}
                options={LEDGER_ACCOUNT_TYPE.map((value) => ({
                  label: t(($) => $.accountTypes[value]),
                  value,
                }))}
                isDisabled={isDeleted}
                isExclusive
              />
            )}
          </form.AppField>
          <form.AppField name="costType">
            {(field) => (
              <field.TextField
                className="flex-1"
                isDisabled={isDeleted}
                label={t(($) => $.form.costType.label)}
                placeholder={t(($) => $.form.costType.placeholder)}
              />
            )}
          </form.AppField>
        </FormRow>
      </Fieldset>

      <form.AppForm>
        {isEditMode ? (
          <form.ActionsRow
            className="mt-8 p-0"
            hasResetButton={false}
            primaryAction={isDeleted ? { disabled: true } : undefined}
            requireDirty
            tertiaryAction={
              isDeleted
                ? undefined
                : {
                    disabled: isDeleting,
                    icon: <Icon metaphor="delete" />,
                    onClick: handleDelete,
                    text: t(($) => $.form.actions.delete),
                  }
            }
            onCancel={onCancel}
          />
        ) : (
          <form.ActionsRow
            className="mt-8 p-0"
            hasResetButton={false}
            requireDirty={false}
            onCancel={onCancel}
          />
        )}
      </form.AppForm>
    </form>
  );
}
